import React, { createContext, useContext, useState, ReactNode } from "react";
import { Order } from "../interfaces/order";
import { OrderForm } from "../interfaces/orderForm";
import { OrderFormType } from "../interfaces/orderFormType";
import { CriminalOrder } from "../interfaces/criminalOrder";
import { CriminalDetail } from "../interfaces/criminalDetail";
import { ReferenceOrder } from "../interfaces/referenceOrder";
import { ReferenceDetail } from "../interfaces/referenceDetaill";
import { Service } from "../interfaces/service";
import { Client } from "../interfaces/client";
import { AdditionalName } from "../interfaces/additionalName";
import { InvoiceProcessType } from "../interfaces/invoiceProcessType";
import { ReferenceType } from "../interfaces/referenceType";
import { OrderService } from "../interfaces/orderService";
import { Vendor } from "../interfaces/vendor";
import { ROCandidateNotifiedDate } from "../interfaces/roCandidateNotifiedDate";
import { SubscriptionDetail } from "../interfaces/subscriptionDetail";
import { SubscriptionOrder } from "../interfaces/subscriptionOrder";

interface ViewOrderContextType {
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
  orderForms: OrderForm[];
  setOrderForms: React.Dispatch<React.SetStateAction<OrderForm[]>>;
  orderFormTypes: OrderFormType[];
  setOrderFormTypes: React.Dispatch<React.SetStateAction<OrderFormType[]>>;
  criminalOrder?: CriminalOrder;
  setCriminalOrder: React.Dispatch<
    React.SetStateAction<CriminalOrder | undefined>
  >;
  criminalDetails: CriminalDetail[];
  setCriminalDetails: React.Dispatch<React.SetStateAction<CriminalDetail[]>>;
  subscriptionOrder?: SubscriptionOrder;
  setSubscriptionOrder: React.Dispatch<
    React.SetStateAction<SubscriptionOrder | undefined>
  >;
  subscriptionDetails: SubscriptionDetail[];
  setSubscriptionDetails: React.Dispatch<
    React.SetStateAction<SubscriptionDetail[]>
  >;
  referenceOrder?: ReferenceOrder;
  setReferenceOrder: React.Dispatch<
    React.SetStateAction<ReferenceOrder | undefined>
  >;
  referenceDetails: ReferenceDetail[];
  setReferenceDetails: React.Dispatch<React.SetStateAction<ReferenceDetail[]>>;
  services: Service[];
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
  client?: Client;
  setClient: React.Dispatch<React.SetStateAction<Client | undefined>>;
  additionalNames: AdditionalName[];
  setAdditionalNames: React.Dispatch<React.SetStateAction<AdditionalName[]>>;
  invoiceProcessTypes: InvoiceProcessType[];
  setInvoiceProcessTypes: React.Dispatch<
    React.SetStateAction<InvoiceProcessType[]>
  >;
  referenceTypes: ReferenceType[];
  setReferenceTypes: React.Dispatch<React.SetStateAction<ReferenceType[]>>;
  orderServices: OrderService[];
  setOrderServices: React.Dispatch<React.SetStateAction<OrderService[]>>;
  vendors: Vendor[];
  setVendors: React.Dispatch<React.SetStateAction<Vendor[]>>;
  signedUrls: Record<number, string>;
  setSignedUrls: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  criminalFinalUrl: string | null;
  setCriminalFinalUrl: React.Dispatch<React.SetStateAction<string | null>>;
  referenceFinalUrl: string | null;
  setReferenceFinalUrl: React.Dispatch<React.SetStateAction<string | null>>;
  deleteDisabled: boolean;
  setDeleteDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  canSendCriminalFinal: boolean;
  setCanSendCriminalFinal: React.Dispatch<React.SetStateAction<boolean>>;
  candidateNotifiedDates?: ROCandidateNotifiedDate[];
  setCandidateNotifiedDates: React.Dispatch<
    React.SetStateAction<ROCandidateNotifiedDate[] | undefined>
  >;
}

const ViewOrderContext = createContext<ViewOrderContextType | undefined>(
  undefined,
);

export const ViewOrderProvider = ({ children }: { children: ReactNode }) => {
  const [order, setOrder] = useState<Order>({
    orderID: 0,
    clientID: 0,
    candidateID: 0,
    initialFormsDate: new Date(),
    initiatedDate: new Date(),
    receivedDate: new Date(),
    designation: "",
    sendByMail: false,
    orderStatusID: 0,
    candidateSSN: "",
    candidateDOB: new Date(),
    candidateTitle: "",
    candidateFirstName: "",
    candidateLastName: "",
    candidateMI: "",
    candidateEmail: "",
    candidatePhone: "",
    candidateAddress: "",
    candidateAddress2: "",
    candidateCity: "",
    candidateState: "",
    candidateZipCode: "",
    candidateCountry: "United States",
    fcraCompliance: false,
    updateMessage: "",
    invoiceProcessTypeID: 0,
    completedDate: new Date(),
    lastUpdatedDate: new Date(),
    requiresCandidatePayment: false,
    paymentReceived: false,
    alert: "",
    alertLastUpdatedDate: new Date(),
  });
  const [orderForms, setOrderForms] = useState<OrderForm[]>([]);
  const [orderFormTypes, setOrderFormTypes] = useState<OrderFormType[]>([]);
  const [criminalOrder, setCriminalOrder] = useState<CriminalOrder>();
  const [criminalDetails, setCriminalDetails] = useState<CriminalDetail[]>([]);
  const [subscriptionOrder, setSubscriptionOrder] =
    useState<SubscriptionOrder>();
  const [subscriptionDetails, setSubscriptionDetails] = useState<
    SubscriptionDetail[]
  >([]);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder>();
  const [referenceDetails, setReferenceDetails] = useState<ReferenceDetail[]>(
    [],
  );
  const [services, setServices] = useState<Service[]>([]);
  const [client, setClient] = useState<Client>();
  const [additionalNames, setAdditionalNames] = useState<AdditionalName[]>([]);
  const [invoiceProcessTypes, setInvoiceProcessTypes] = useState<
    InvoiceProcessType[]
  >([]);
  const [referenceTypes, setReferenceTypes] = useState<ReferenceType[]>([]);
  const [orderServices, setOrderServices] = useState<OrderService[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [criminalFinalUrl, setCriminalFinalUrl] = useState<string | null>(null);
  const [referenceFinalUrl, setReferenceFinalUrl] = useState<string | null>(
    null,
  );
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [canSendCriminalFinal, setCanSendCriminalFinal] = useState(false);
  const [candidateNotifiedDates, setCandidateNotifiedDates] =
    useState<ROCandidateNotifiedDate[]>();

  return (
    <ViewOrderContext.Provider
      value={{
        order,
        setOrder,
        orderForms,
        setOrderForms,
        orderFormTypes,
        setOrderFormTypes,
        criminalOrder,
        setCriminalOrder,
        criminalDetails,
        setCriminalDetails,
        subscriptionOrder,
        setSubscriptionOrder,
        subscriptionDetails,
        setSubscriptionDetails,
        referenceOrder,
        setReferenceOrder,
        referenceDetails,
        setReferenceDetails,
        services,
        setServices,
        client,
        setClient,
        additionalNames,
        setAdditionalNames,
        invoiceProcessTypes,
        setInvoiceProcessTypes,
        referenceTypes,
        setReferenceTypes,
        orderServices,
        setOrderServices,
        vendors,
        setVendors,
        signedUrls,
        setSignedUrls,
        criminalFinalUrl,
        setCriminalFinalUrl,
        referenceFinalUrl,
        setReferenceFinalUrl,
        deleteDisabled,
        setDeleteDisabled,
        canSendCriminalFinal,
        setCanSendCriminalFinal,
        candidateNotifiedDates,
        setCandidateNotifiedDates,
      }}
    >
      {children}
    </ViewOrderContext.Provider>
  );
};

export const useViewOrderContext = () => {
  const context = useContext(ViewOrderContext);
  if (!context) {
    throw new Error(
      "useViewOrderContext must be used within a ViewOrderProvider",
    );
  }
  return context;
};
